import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchConveyancingBatches, createConveyancingBatch,
  updateConveyancingBatch, deleteConveyancingBatch,
  fetchBatchEligibleMatters, addBatchMember, removeBatchMember,
  setBatchMemberReady,
} from '../api';
import { useConfirm } from './ConfirmProvider';

const BLANK = { name: '', lodgement_date: '', all_ready: false, notes: '' };

export default function ConveyancingBatchManager({ authAxios, showFormMessage }) {
  const confirm = useConfirm();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchConveyancingBatches(authAxios);
      setBatches(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage?.('error', 'Failed to load batches.');
    } finally {
      setLoading(false);
    }
  }, [authAxios, showFormMessage]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(BLANK); setShowForm(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...BLANK, ...b }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.lodgement_date) payload.lodgement_date = null;

      if (editing) {
        const updated = await updateConveyancingBatch(authAxios, editing.id, payload);
        showFormMessage?.('success', 'Batch updated.');
        setSelected(updated);
      } else {
        await createConveyancingBatch(authAxios, payload);
        showFormMessage?.('success', 'Batch created.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail ?? JSON.stringify(err.response?.data ?? err.message);
      showFormMessage?.('error', `Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (b) => {
    const ok = await confirm({ title: 'Delete batch?', message: `Delete batch "${b.name}"?` });
    if (!ok) return;
    try {
      await deleteConveyancingBatch(authAxios, b.id);
      showFormMessage?.('success', 'Batch deleted.');
      if (selected?.id === b.id) setSelected(null);
      load();
    } catch {
      showFormMessage?.('error', 'Failed to delete batch.');
    }
  };

  const toggleMemberReady = async (batch, member) => {
    try {
      const updated = await setBatchMemberReady(authAxios, batch.id, member.id, !member.is_ready);
      setSelected(updated);
      // Refresh list so aggregated badges update
      load();
    } catch {
      showFormMessage?.('error', 'Failed to update member status.');
    }
  };

  // ── Add-member flow ────────────────────────────────────────────────────
  const [showAddMember, setShowAddMember] = useState(false);
  const [eligibleMatters, setEligibleMatters] = useState([]);
  const [addingMember, setAddingMember] = useState(false);
  const [externalFirmName, setExternalFirmName] = useState('');
  const [externalFirmContact, setExternalFirmContact] = useState('');

  const openAddMember = async () => {
    if (!selected) return;
    setShowAddMember(true);
    try {
      const data = await fetchBatchEligibleMatters(authAxios, selected.id);
      setEligibleMatters(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage?.('error', 'Failed to load eligible matters.');
      setEligibleMatters([]);
    }
  };

  const handleAddMember = async (matterId) => {
    if (!selected) return;
    setAddingMember(true);
    try {
      const updated = await addBatchMember(authAxios, selected.id, {
        conveyancing_detail: matterId,
        external_firm_name: externalFirmName,
        external_firm_contact: externalFirmContact,
      });
      setSelected(updated);
      setShowAddMember(false);
      setExternalFirmName('');
      setExternalFirmContact('');
      showFormMessage?.('success', 'Matter linked to batch.');
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail ?? 'Failed to add member.';
      showFormMessage?.('error', msg);
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!selected) return;
    const ok = await confirm({ title: 'Unlink matter from batch?', message: 'This matter will be removed from this lodgement batch.' });
    if (!ok) return;
    try {
      const updated = await removeBatchMember(authAxios, selected.id, memberId);
      setSelected(updated);
      load();
    } catch {
      showFormMessage?.('error', 'Failed to remove member.');
    }
  };

  const fld = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Simultaneous Lodgement Batches</h2>
          <p className="text-sm text-gray-500 mt-0.5">Link transfer + bond + cancellation for simultaneous Deeds Registry lodgement</p>
        </div>
        <button onClick={openCreate}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700">
          + New Batch
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Batch list */}
        <div className="space-y-3">
          {loading && <p className="text-sm text-gray-400">Loading…</p>}
          {batches.map(b => {
            const totalMembers = b.members?.length ?? 0;
            const readyCount = b.members?.filter(m => m.is_ready).length ?? 0;
            const r = b.readiness_summary || {};
            return (
              <button key={b.id} onClick={() => setSelected(b)}
                className={`w-full text-left border rounded-xl p-4 hover:shadow-sm transition ${selected?.id === b.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium text-gray-800 text-sm">{b.name}</span>
                  {b.all_ready ? (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Ready</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">{readyCount}/{totalMembers} ready</span>
                  )}
                </div>
                {b.lodgement_date && (
                  <div className="mt-1 text-xs text-gray-500">Lodgement: {b.lodgement_date}</div>
                )}
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-500 h-1.5 rounded-full transition-all"
                    style={{ width: totalMembers > 0 ? `${(readyCount / totalMembers) * 100}%` : '0%' }}
                  />
                </div>
                {/* Aggregated blockers — what's missing across the batch */}
                {(r.missing_fia || r.missing_burs || r.missing_property_link || r.missing_instruments) ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {r.missing_fia > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700">{r.missing_fia} FIA</span>}
                    {r.missing_burs > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{r.missing_burs} BURS</span>}
                    {r.missing_property_link > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-700">{r.missing_property_link} plot</span>}
                    {r.missing_instruments > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">{r.missing_instruments} no instruments</span>}
                  </div>
                ) : totalMembers > 0 ? (
                  <div className="mt-2 text-[10px] text-green-700">✓ All members have FIA, BURS, plot & instruments</div>
                ) : null}
              </button>
            );
          })}
          {!loading && batches.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">No batches yet.</p>
          )}
        </div>

        {/* Batch detail */}
        <div className="lg:col-span-2">
          {selected ? (
            <div className="border border-gray-200 rounded-xl bg-white p-5 space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{selected.name}</h3>
                  {selected.lodgement_date && (
                    <p className="text-sm text-gray-500 mt-0.5">Lodgement date: {selected.lodgement_date}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(selected)}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">Edit</button>
                  <button onClick={() => handleDelete(selected)}
                    className="px-3 py-1.5 text-xs border border-red-300 text-red-600 rounded-lg hover:bg-red-50">Delete</button>
                </div>
              </div>

              {selected.notes && (
                <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{selected.notes}</p>
              )}

              {/* Members */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Batch Members</h4>
                  <button onClick={openAddMember}
                    className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700">
                    + Link Matter
                  </button>
                </div>
                {(selected.members ?? []).length === 0 ? (
                  <p className="text-sm text-gray-400">No matters linked to this batch yet. Link via the Conveyancing Detail panel on each matter.</p>
                ) : (
                  <div className="space-y-3">
                    {selected.members.map(m => {
                      const ctx = m.matter_context;
                      const parties = ctx?.parties || {};
                      const readiness = ctx?.readiness || {};
                      const instruments = ctx?.instruments || [];
                      const blockers = [];
                      if (!readiness.fia_kyc_complete) blockers.push('FIA');
                      if (!readiness.burs_receipt_present) blockers.push('BURS');
                      if (!readiness.has_property_link) blockers.push('Plot link');
                      if (!readiness.has_at_least_one_instrument) blockers.push('Instruments');
                      if (!readiness.has_buyer_party || !readiness.has_seller_party) blockers.push('Parties');

                      return (
                        <div key={m.id} className="border border-gray-100 rounded-lg p-4 bg-white">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-gray-900">
                                  {ctx?.case_number || `Matter #${m.conveyancing_detail}`}
                                </span>
                                {ctx?.transaction_type && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                                    {ctx.transaction_type.replace(/_/g, ' ')}
                                  </span>
                                )}
                                {ctx?.case_stage && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                    {ctx.case_stage}
                                  </span>
                                )}
                              </div>
                              {ctx?.case_title && (
                                <div className="text-xs text-gray-600 mt-0.5 truncate">{ctx.case_title}</div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={m.is_ready}
                                  onChange={() => toggleMemberReady(selected, m)}
                                  className="w-4 h-4 accent-blue-600"
                                />
                                <span className="text-xs text-gray-600">Ready</span>
                              </label>
                              <button
                                onClick={() => handleRemoveMember(m.id)}
                                title="Unlink from batch"
                                className="text-xs text-red-500 hover:underline"
                              >Remove</button>
                            </div>
                          </div>

                          {/* Property + amounts */}
                          {ctx?.property ? (
                            <div className="text-xs text-gray-700 grid grid-cols-2 gap-x-3 gap-y-1 mb-2 bg-gray-50 rounded p-2">
                              <div><span className="text-gray-400">Plot:</span> <strong>{ctx.property.plot_number}</strong></div>
                              <div><span className="text-gray-400">District:</span> {ctx.property.district || '—'}</div>
                              <div><span className="text-gray-400">Deed:</span> <span className="font-mono">{ctx.property.deed_of_transfer_number || '—'}</span></div>
                              <div><span className="text-gray-400">Tenure:</span> {ctx.property.tenure_type || '—'}</div>
                              {ctx.property_value && <div><span className="text-gray-400">Value:</span> BWP {Number(ctx.property_value).toLocaleString()}</div>}
                              {ctx.bond_amount && <div><span className="text-gray-400">Bond:</span> BWP {Number(ctx.bond_amount).toLocaleString()}</div>}
                            </div>
                          ) : (
                            <div className="text-[11px] text-amber-700 bg-amber-50 rounded p-2 mb-2">
                              No property linked to this matter.
                            </div>
                          )}

                          {/* Parties summary */}
                          {(parties.sellers?.length || parties.buyers?.length) && (
                            <div className="text-xs text-gray-600 mb-2 space-y-0.5">
                              {parties.sellers?.length > 0 && <div><span className="text-gray-400">Seller(s):</span> {parties.sellers.join(', ')}</div>}
                              {parties.buyers?.length > 0 && <div><span className="text-gray-400">Buyer(s):</span> {parties.buyers.join(', ')}</div>}
                              {parties.estate_agents?.length > 0 && <div><span className="text-gray-400">Agent(s):</span> {parties.estate_agents.join(', ')}</div>}
                            </div>
                          )}

                          {/* Deed instruments */}
                          {instruments.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {instruments.map(ins => (
                                <span key={ins.id} className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-100">
                                  {ins.instrument_type.replace(/_/g, ' ')}
                                  {ins.instrument_number ? ` · ${ins.instrument_number}` : ''}
                                  {ins.status ? ` · ${ins.status}` : ''}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* External firm */}
                          {m.external_firm_name && (
                            <div className="text-xs text-gray-500 mb-2">
                              External firm: <strong>{m.external_firm_name}</strong>
                              {m.external_firm_contact && ` · ${m.external_firm_contact}`}
                            </div>
                          )}

                          {/* Blocker badges */}
                          {blockers.length > 0 ? (
                            <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-gray-100">
                              <span className="text-[10px] text-gray-500">Missing:</span>
                              {blockers.map(b => (
                                <span key={b} className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700">{b}</span>
                              ))}
                            </div>
                          ) : (
                            <div className="text-[10px] text-green-700 mt-2 pt-2 border-t border-gray-100">
                              ✓ FIA, BURS, plot link, parties and instruments all present — ready for lodgement
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
              Select a batch to view details
            </div>
          )}
        </div>
      </div>

      {/* Add Member modal */}
      {showAddMember && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="px-6 py-5 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Link a Matter to {selected.name}</h3>
                <p className="text-xs text-gray-500 mt-0.5">Pick a conveyancing matter to include in this lodgement batch.</p>
              </div>
              <button onClick={() => setShowAddMember(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">External Firm Name (optional)</label>
                  <input value={externalFirmName} onChange={e => setExternalFirmName(e.target.value)}
                    placeholder="If a different firm handles this leg"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">External Firm Contact (optional)</label>
                  <input value={externalFirmContact} onChange={e => setExternalFirmContact(e.target.value)}
                    placeholder="Email or phone"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                </div>
              </div>

              <div className="border border-gray-200 rounded-md max-h-80 overflow-y-auto">
                {eligibleMatters.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No eligible matters available.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {eligibleMatters.map(em => (
                      <li key={em.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {em.case_number || `Matter #${em.id}`}
                            <span className="ml-2 text-xs text-gray-500">{em.transaction_type?.replace(/_/g, ' ')}</span>
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">{em.case_title}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {em.plot_number ? `Plot: ${em.plot_number}` : <span className="text-amber-700">No plot linked</span>}
                            {' · '}
                            {em.fica_kyc_complete ? <span className="text-green-700">FIA ✓</span> : <span className="text-red-600">FIA missing</span>}
                            {' · '}
                            {em.burs_receipt_number ? <span className="text-green-700">BURS ✓</span> : <span className="text-amber-700">BURS pending</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddMember(em.id)}
                          disabled={addingMember}
                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
                        >
                          {addingMember ? '…' : 'Link'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button onClick={() => setShowAddMember(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-5 border-b flex items-center justify-between">
              <h3 className="font-semibold">{editing ? 'Edit Batch' : 'New Batch'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Batch Name *</label>
                <input required value={form.name} onChange={e => fld('name', e.target.value)}
                  placeholder="e.g. Plot 5432 Transfer + Bond"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Planned Lodgement Date</label>
                <input type="date" value={form.lodgement_date} onChange={e => fld('lodgement_date', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.all_ready} onChange={e => fld('all_ready', e.target.checked)}
                  className="w-4 h-4 accent-blue-600" />
                <span className="text-sm text-gray-700">All members ready for lodgement</span>
              </label>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => fld('notes', e.target.value)}
                  rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
                  {saving ? 'Saving…' : editing ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
